// gym/GymShopProducts.jsx - Version corrigée
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Button, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField,  Alert, Snackbar, Chip,
  FormControl, InputLabel, Select, MenuItem,
  Tooltip, Card, CardContent,
  CircularProgress, InputAdornment,
  Avatar, Badge
} from '@mui/material';
// MUI v7 : le `Grid` par défaut a perdu les props `item` / `xs` / `md`
// au profit de `size={{...}}`. Ce fichier utilise l'ancienne API.
import { GridLegacy as Grid } from '@mui/material';
import {
  Add, Edit, Delete, Inventory, CheckCircle,
  Cancel, Search, Refresh, LocalOffer,
  ShoppingCart, TrendingUp, Category,
  AttachMoney, Numbers, Image
} from '@mui/icons-material';
import api from '../services/auth';

const GymShopProducts = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [stockFilter, setStockFilter] = useState('all');
  const [formData, setFormData] = useState({
    name: '',
    category: 'SUPPLEMENT',
    description: '',
    price_htg: '0.00',
    cost_price_htg: '',
    stock_quantity: 0,
    min_stock_level: 5,
    is_active: true
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const CATEGORY_CHOICES = [
    { value: 'SUPPLEMENT', label: 'Supplément', color: 'primary' },
    { value: 'ACCESSORY', label: 'Accessoire', color: 'secondary' },
    { value: 'CLOTHING', label: 'Vêtement', color: 'success' },
    { value: 'EQUIPMENT', label: 'Équipement', color: 'warning' }
  ];

  const STOCK_STATUS = {
    IN_STOCK: { label: 'En stock', color: 'success' },
    LOW_STOCK: { label: 'Stock faible', color: 'warning' },
    OUT_OF_STOCK: { label: 'Rupture', color: 'error' }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    filterProducts();
  }, [products, searchTerm, statusFilter, categoryFilter, stockFilter]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      console.log('🔍 Fetching products from API...');
      
      const response = await api.get('/gym/shop-products/');
      
      console.log('✅ API Response status:', response.status);
      console.log('📊 API Response data:', response.data);
      console.log('📈 Response data type:', typeof response.data);
      
      let productsData = [];
      
      if (response.data && typeof response.data === 'object') {
        if (response.data.results) {
          // Pagination Django REST framework
          productsData = response.data.results;
          console.log('📄 Pagination detected, total products:', response.data.count);
          console.log('📄 Results array:', response.data.results);
        } else if (Array.isArray(response.data)) {
          // Déjà un tableau
          productsData = response.data;
        }
      }
      
      console.log('📦 Processed products data:', productsData);
      console.log('🛒 Number of products:', productsData.length);
      
      // Stocker directement le tableau
      setProducts(productsData);
      
    } catch (error) {
      console.error('❌ Error fetching products:', error);
      console.error('🔍 Error details:', error.response?.data);
      setSnackbar({
        open: true,
        message: 'Erreur lors du chargement des produits',
        severity: 'error'
      });
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  // Fonction utilitaire pour obtenir un tableau de produits
  const getProductsArray = () => {
    return Array.isArray(products) ? products : [];
  };

  const filterProducts = () => {
    console.log('🔍 Starting filterProducts...');
    
    const productsArray = getProductsArray();
    console.log('📦 Products array for filtering:', productsArray);
    console.log('🔢 Products array length:', productsArray.length);
    
    let filtered = productsArray;
    
    if (searchTerm) {
      filtered = filtered.filter(product =>
        (product.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
        (product.code?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
        (product.description?.toLowerCase() || '').includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(product =>
        statusFilter === 'active' ? product.is_active : !product.is_active
      );
    }

    if (categoryFilter !== 'all') {
      filtered = filtered.filter(product => product.category === categoryFilter);
    }

    if (stockFilter !== 'all') {
      filtered = filtered.filter(product => {
        const stockStatus = getStockStatus(product.stock_quantity, product.min_stock_level);
        return stockStatus === stockFilter;
      });
    }

    console.log('✅ Filtered products:', filtered);
    console.log('🔢 Filtered length:', filtered.length);
    
    setFilteredProducts(filtered);
  };

  const handleOpenDialog = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setFormData({
        name: product.name || '',
        category: product.category || 'SUPPLEMENT',
        description: product.description || '',
        price_htg: product.price_htg || '0.00',
        cost_price_htg: product.cost_price_htg || '',
        stock_quantity: product.stock_quantity || 0,
        min_stock_level: product.min_stock_level || 5,
        is_active: product.is_active !== undefined ? product.is_active : true
      });
    } else {
      setEditingProduct(null);
      setFormData({
        name: '',
        category: 'SUPPLEMENT',
        description: '',
        price_htg: '0.00',
        cost_price_htg: '',
        stock_quantity: 0,
        min_stock_level: 5,
        is_active: true
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingProduct(null);
    setSubmitting(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Préparer les données pour l'API
      const apiData = {
        name: formData.name,
        category: formData.category,
        description: formData.description,
        price_htg: parseFloat(formData.price_htg) || 0,
        cost_price_htg: formData.cost_price_htg ? parseFloat(formData.cost_price_htg) : null,
        stock_quantity: parseInt(formData.stock_quantity) || 0,
        min_stock_level: parseInt(formData.min_stock_level) || 5,
        is_active: formData.is_active
      };

      console.log('🚀 Submitting product data:', apiData);

      let response;
      if (editingProduct) {
        response = await api.put(`/gym/shop-products/${editingProduct.id}/`, apiData);
        console.log('✅ Update response:', response.data);
        console.log('✅ Update status:', response.status);
        setSnackbar({ open: true, message: 'Produit modifié avec succès', severity: 'success' });
      } else {
        response = await api.post('/gym/shop-products/', apiData);
        console.log('✅ Create response:', response.data);
        console.log('✅ Create status:', response.status);
        console.log('✅ New product ID:', response.data.id);
        console.log('✅ New product code:', response.data.code);
        setSnackbar({ open: true, message: 'Produit créé avec succès', severity: 'success' });
      }

      handleCloseDialog();
      
      // Rafraîchir la liste après un court délai
      setTimeout(() => {
        console.log('🔄 Refreshing products list...');
        fetchProducts();
      }, 500);

    } catch (error) {
      console.error('❌ Error submitting product:', error);
      console.error('🔍 Error details:', error.response?.data);

      let errorMessage = 'Erreur lors de l\'opération';
      if (error.response?.data) {
        if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else {
          const errors = Object.values(error.response.data).flat();
          errorMessage = errors.join(', ');
        }
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce produit?')) {
      try {
        console.log('🗑️ Deleting product:', id);
        await api.delete(`/gym/shop-products/${id}/`);
        setSnackbar({ open: true, message: 'Produit supprimé avec succès', severity: 'success' });
        fetchProducts();
      } catch (error) {
        console.error('Error deleting product:', error);
        setSnackbar({ open: true, message: 'Erreur lors de la suppression', severity: 'error' });
      }
    }
  };

  const getCategoryLabel = (value) => {
    const category = CATEGORY_CHOICES.find(c => c.value === value);
    return category ? category.label : value;
  };

  const getCategoryColor = (value) => {
    const category = CATEGORY_CHOICES.find(c => c.value === value);
    return category ? category.color : 'default';
  };

  const getStockStatus = (stockQuantity, minStockLevel) => {
    if (stockQuantity <= 0) return 'OUT_OF_STOCK';
    if (stockQuantity <= minStockLevel) return 'LOW_STOCK';
    return 'IN_STOCK';
  };

  const getStockLabel = (stockQuantity, minStockLevel) => {
    const status = getStockStatus(stockQuantity, minStockLevel);
    return STOCK_STATUS[status]?.label || status;
  };

  const getStockColor = (stockQuantity, minStockLevel) => {
    const status = getStockStatus(stockQuantity, minStockLevel);
    return STOCK_STATUS[status]?.color || 'default';
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return 'N/A';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'HTG',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const calculateProfitMargin = (price, cost) => {
    if (!cost || cost <= 0) return null;
    const margin = ((price - cost) / cost) * 100;
    return margin.toFixed(1);
  };

  // Calcul des statistiques
  const productsArray = getProductsArray();
  const stats = {
    total: productsArray.length,
    active: productsArray.filter(p => p.is_active).length,
    totalStockValue: productsArray.reduce((total, p) => total + (p.price_htg * p.stock_quantity), 0),
    lowStock: productsArray.filter(p => getStockStatus(p.stock_quantity, p.min_stock_level) === 'LOW_STOCK').length,
    outOfStock: productsArray.filter(p => getStockStatus(p.stock_quantity, p.min_stock_level) === 'OUT_OF_STOCK').length
  };

  // Catégories pour le filtre
  const categories = [
    { value: 'all', label: 'Toutes catégories' },
    ...CATEGORY_CHOICES.map(cat => ({ value: cat.value, label: cat.label }))
  ];

  return (
    <Box p={3}>
      <Box sx={{ mb: 2 }}>
      </Box>

      {/* Statistiques */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <Inventory color="primary" />
                <Typography variant="h6">Total Produits</Typography>
              </Box>
              <Typography variant="h4" color="primary.main">
                {stats.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card sx={{ backgroundColor: 'warning.light', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <TrendingUp />
                <Typography variant="h6">Valeur Stock</Typography>
              </Box>
              <Typography variant="h4">
                {formatCurrency(stats.totalStockValue)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card sx={{ backgroundColor: 'error.light', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <Cancel />
                <Typography variant="h6">Rupture Stock</Typography>
              </Box>
              <Typography variant="h4">
                {stats.outOfStock}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card sx={{ backgroundColor: 'success.light', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <CheckCircle />
                <Typography variant="h6">Produits Actifs</Typography>
              </Box>
              <Typography variant="h4">
                {stats.active}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* En-tête */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">🛍️ Produits Boutique GYM</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
          disabled={submitting}
        >
          {submitting ? <CircularProgress size={24} /> : 'Nouveau Produit'}
        </Button>
      </Box>

      {/* Barre de recherche et filtres */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              placeholder="Rechercher produit, code, description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'action.active' }} />,
              }}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Catégorie</InputLabel>
              <Select
                value={categoryFilter}
                label="Catégorie"
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                {categories.map(cat => (
                  <MenuItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Statut Stock</InputLabel>
              <Select
                value={stockFilter}
                label="Statut Stock"
                onChange={(e) => setStockFilter(e.target.value)}
              >
                <MenuItem value="all">Tous</MenuItem>
                <MenuItem value="IN_STOCK">En stock</MenuItem>
                <MenuItem value="LOW_STOCK">Stock faible</MenuItem>
                <MenuItem value="OUT_OF_STOCK">Rupture</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Statut</InputLabel>
              <Select
                value={statusFilter}
                label="Statut"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">Tous</MenuItem>
                <MenuItem value="active">Actifs</MenuItem>
                <MenuItem value="inactive">Inactifs</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchProducts}
              disabled={loading}
            >
              {loading ? <CircularProgress size={20} /> : 'Actualiser'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Tableau */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'primary.light' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Code</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Produit</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Catégorie</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Prix & Stock</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Marge</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Statut Stock</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Statut</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                  <Typography sx={{ mt: 2 }}>Chargement des produits...</Typography>
                </TableCell>
              </TableRow>
            ) : !Array.isArray(filteredProducts) || filteredProducts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  {productsArray.length === 0 ? (
                    <Box>
                      <Typography variant="h6" color="textSecondary" gutterBottom>
                        Aucun produit enregistré
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Cliquez sur "Nouveau Produit" pour créer votre premier produit.
                      </Typography>
                    </Box>
                  ) : (
                    <Box>
                      <Typography variant="h6" color="textSecondary" gutterBottom>
                        Aucun produit trouvé avec les filtres actuels
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Total produits: {productsArray.length}
                      </Typography>
                      <Button 
                        variant="outlined" 
                        sx={{ mt: 2 }}
                        onClick={() => {
                          setSearchTerm('');
                          setStatusFilter('all');
                          setCategoryFilter('all');
                          setStockFilter('all');
                        }}
                      >
                        Réinitialiser les filtres
                      </Button>
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              filteredProducts.map((product) => {
                const margin = calculateProfitMargin(product.price_htg, product.cost_price_htg);
                const stockStatus = getStockStatus(product.stock_quantity, product.min_stock_level);
                const stockLabel = getStockLabel(product.stock_quantity, product.min_stock_level);
                const stockColor = getStockColor(product.stock_quantity, product.min_stock_level);
                
                return (
                  <TableRow key={product.id} hover>
                    <TableCell>
                      <Chip 
                        label={product.code || 'N/A'} 
                        color="primary" 
                        size="small" 
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography fontWeight="bold">
                          {product.name}
                        </Typography>
                        {product.description && (
                          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                            {product.description.length > 50 
                              ? `${product.description.substring(0, 50)}...` 
                              : product.description}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getCategoryLabel(product.category)}
                        color={getCategoryColor(product.category)}
                        size="small"
                        icon={<Category fontSize="small" />}
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" flexDirection="column" gap={0.5}>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <AttachMoney fontSize="small" color="action" />
                          <Typography fontWeight="bold">
                            {formatCurrency(product.price_htg)}
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Numbers fontSize="small" color="action" />
                          <Typography>
                            Stock: {product.stock_quantity}
                          </Typography>
                          {product.min_stock_level > 0 && (
                            <Typography variant="caption" color="textSecondary">
                              (min: {product.min_stock_level})
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {product.cost_price_htg ? (
                        <Box>
                          <Typography variant="body2">
                            Coût: {formatCurrency(product.cost_price_htg)}
                          </Typography>
                          {margin && (
                            <Chip
                              label={`${margin}%`}
                              color={parseFloat(margin) > 50 ? 'success' : 'warning'}
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          Coût non défini
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={stockLabel}
                        color={stockColor}
                        size="small"
                        icon={stockStatus === 'IN_STOCK' ? <CheckCircle /> : 
                              stockStatus === 'LOW_STOCK' ? <TrendingUp /> : <Cancel />}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={product.is_active ? 'Actif' : 'Inactif'}
                        color={product.is_active ? 'success' : 'error'}
                        size="small"
                        icon={product.is_active ? <CheckCircle /> : <Cancel />}
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <Tooltip title="Modifier">
                          <IconButton
                            color="primary"
                            size="small"
                            onClick={() => handleOpenDialog(product)}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Supprimer">
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() => handleDelete(product.id)}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog pour créer/modifier */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingProduct ? '✏️ Modifier Produit' : '➕ Nouveau Produit'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Nom du produit *"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  margin="normal"
                  placeholder="Ex: Protéine Whey 1kg, Short de sport..."
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal" required>
                  <InputLabel>Catégorie *</InputLabel>
                  <Select
                    value={formData.category}
                    label="Catégorie *"
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  >
                    {CATEGORY_CHOICES.map(category => (
                      <MenuItem key={category.value} value={category.value}>
                        {category.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Quantité en stock *"
                  type="number"
                  value={formData.stock_quantity}
                  onChange={(e) => setFormData({ ...formData, stock_quantity: e.target.value })}
                  required
                  margin="normal"
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Prix de vente (HTG) *"
                  type="number"
                  value={formData.price_htg}
                  onChange={(e) => setFormData({ ...formData, price_htg: e.target.value })}
                  required
                  margin="normal"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">HTG</InputAdornment>,
                  }}
                  inputProps={{ min: "0.01", step: "0.01" }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Prix de revient (HTG)"
                  type="number"
                  value={formData.cost_price_htg}
                  onChange={(e) => setFormData({ ...formData, cost_price_htg: e.target.value })}
                  margin="normal"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">HTG</InputAdornment>,
                  }}
                  inputProps={{ min: "0.01", step: "0.01" }}
                  placeholder="Optionnel"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Stock minimum *"
                  type="number"
                  value={formData.min_stock_level}
                  onChange={(e) => setFormData({ ...formData, min_stock_level: e.target.value })}
                  required
                  margin="normal"
                  inputProps={{ min: 0 }}
                  helperText="Alerte quand stock ≤ cette valeur"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Statut</InputLabel>
                  <Select
                    value={formData.is_active}
                    label="Statut"
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.value })}
                  >
                    <MenuItem value={true}>Actif</MenuItem>
                    <MenuItem value={false}>Inactif</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description (optionnel)"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  multiline
                  rows={3}
                  margin="normal"
                  placeholder="Description détaillée du produit..."
                />
              </Grid>
              {formData.price_htg && formData.cost_price_htg && (
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Récapitulatif financier :
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="body2">
                          Prix vente: {formatCurrency(formData.price_htg)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="body2">
                          Prix revient: {formatCurrency(formData.cost_price_htg)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="body2">
                          Marge: {calculateProfitMargin(formData.price_htg, formData.cost_price_htg)}%
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2">
                          Valeur stock: {formatCurrency(formData.price_htg * formData.stock_quantity)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} disabled={submitting}>
              Annuler
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={submitting || !formData.name || !formData.price_htg}
            >
              {submitting ? <CircularProgress size={24} /> : editingProduct ? 'Modifier' : 'Créer'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GymShopProducts;